import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X } from 'lucide-react';

const ReviewPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    // Check if we should show the review prompt
    const checkReviewPrompt = () => {
      const reviewData = JSON.parse(localStorage.getItem('nexi_review_data') || '{}');
      const { 
        messageCount = 0, 
        hasRated = false, 
        lastPromptDate = null,
        dismissCount = 0 
      } = reviewData;

      // Don't show if already rated
      if (hasRated) return;

      // Don't show if dismissed 3+ times
      if (dismissCount >= 3) return;

      // Don't show more than once per 3 days
      if (lastPromptDate) {
        const daysSinceLastPrompt = (Date.now() - new Date(lastPromptDate).getTime()) / (1000 * 60 * 60 * 24);
        if (daysSinceLastPrompt < 3) return;
      }

      // Show after 5+ messages
      if (messageCount >= 5) {
        setTimeout(() => setShowPrompt(true), 2000); // Delay for better UX
      }
    };

    checkReviewPrompt();
  }, []);

  const handleRate = () => {
    // Open App Store for rating
    const appStoreUrl = 'https://apps.apple.com/app/id6758422744?action=write-review';
    window.open(appStoreUrl, '_blank');
    
    // Mark as rated
    const reviewData = JSON.parse(localStorage.getItem('nexi_review_data') || '{}');
    reviewData.hasRated = true;
    localStorage.setItem('nexi_review_data', JSON.stringify(reviewData));
    
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    const reviewData = JSON.parse(localStorage.getItem('nexi_review_data') || '{}');
    reviewData.dismissCount = (reviewData.dismissCount || 0) + 1;
    reviewData.lastPromptDate = new Date().toISOString();
    localStorage.setItem('nexi_review_data', JSON.stringify(reviewData));
    
    setShowPrompt(false);
  };

  const handleLater = () => {
    const reviewData = JSON.parse(localStorage.getItem('nexi_review_data') || '{}');
    reviewData.lastPromptDate = new Date().toISOString();
    localStorage.setItem('nexi_review_data', JSON.stringify(reviewData));
    
    setShowPrompt(false);
  };

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={handleDismiss}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-b from-[#1a0a2e] to-[#0a0118] border border-purple-500/30 rounded-2xl p-6 shadow-2xl w-full max-w-sm"
          >
            {/* Close Button */}
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 p-2 hover:bg-white/10 rounded-full"
            >
              <X size={20} className="text-white/50" />
            </button>

            {/* Stars */}
            <div className="flex justify-center mb-4">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={32}
                    className="text-yellow-400 fill-yellow-400"
                  />
                ))}
              </div>
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-center text-white mb-2">
              Enjoying Nexi AI?
            </h2>
            
            <p className="text-center text-white/60 mb-6 text-sm">
              Your feedback helps us improve and helps others discover Nexi!
            </p>

            {/* Rate Button */}
            <motion.button
              onClick={handleRate}
              className="w-full py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold rounded-xl shadow-lg mb-3"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              ⭐ Rate on App Store
            </motion.button>

            {/* Later Button */}
            <button
              onClick={handleLater}
              className="w-full py-2 text-white/50 text-sm hover:text-white/70 transition-colors"
            >
              Maybe Later
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Helper function to increment message count (call this after each AI response)
export const incrementMessageCount = () => {
  const reviewData = JSON.parse(localStorage.getItem('nexi_review_data') || '{}');
  reviewData.messageCount = (reviewData.messageCount || 0) + 1;
  localStorage.setItem('nexi_review_data', JSON.stringify(reviewData));
};

export default ReviewPrompt;
